import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    HeadingSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";

// the invalid course ID used to represent the "general" checkbox on the class
// creator course picker
const GENERAL_COURSE_ID = "non-math";

function Spinner() {
    return (
        <View style={styles.spinnerContainer}>
            <CircularSpinner size="medium" />
        </View>
    );
}

const CourseProgressesList = React.lazy(
    () =>
        import(
            "#content-library-legacy/modules/courses-dashboard/course-progresses-list.tsx"
        ),
);

type Props = {
    classDescriptor: string;
    courseIds: Array<string> | null;
};

export default function CoursesForClass({
    classDescriptor,
    courseIds,
}: Props): React.ReactElement | null {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    let mainContent: React.ReactElement;

    const validCourseIds = courseIds?.filter(
        (courseId) => courseId !== GENERAL_COURSE_ID,
    );

    if (validCourseIds == null) {
        mainContent = <Spinner />;
    } else if (validCourseIds.length === 0) {
        mainContent = (
            <View>
                <Banner
                    text={
                        <>
                            <HeadingSmall style={styles.noCoursesHeading}>
                                {t`No courses added yet`}
                            </HeadingSmall>
                            {t`Your teacher hasn’t added any courses to this class yet. Ask your teacher to add a course to get started!`}
                        </>
                    }
                    kind="info"
                    layout="floating"
                />

                <Strut size={32} />
            </View>
        );
    } else {
        mainContent = (
            <React.Suspense fallback={<Spinner />}>
                <CourseProgressesList
                    courseIds={validCourseIds}
                    // prevent courses from the previous class from rendering
                    // while courses from the current class are loading
                    key={classDescriptor}
                    onNavigationClicked={async (navigationElement) => {
                        // TODO(CL-924): update this to be accurate once we update
                        // the start/resume buttons to link to learnable content
                        await publishAnalyticsEvent({
                            eventType:
                                "LoggedInHomepageClassCoursesContentNavClicked",
                            eventSchemaVersion: 1,
                            classroomDescriptor: classDescriptor,
                            navigationElement,
                            destinationContentKind_contentKind:
                                navigationElement === "SEE_ALL_LINK"
                                    ? "Course"
                                    : "Unit",
                        });
                    }}
                />
            </React.Suspense>
        );
    }

    return (
        <>
            <HeadingLarge style={styles.heading} tag="h3">
                {t`Class courses`}
            </HeadingLarge>
            {mainContent}
        </>
    );
}

const styles = StyleSheet.create({
    heading: {
        marginBottom: spacing.large_24,
    },
    spinnerContainer: {
        alignItems: "center",
        // magic number to match the route load spinner
        marginTop: 40,
        marginBottom: 40,
        marginLeft: spacing.small_12,
        marginRight: spacing.small_12,
    },
    noCoursesHeading: {
        marginBottom: spacing.xxSmall_6,
    },
});
